import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Check, Clock, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../auth/authStore';
import { formatLocalTime } from '../../../shared/lib/datetime';
import { useI18nStore } from '../../../shared/i18n/i18nStore';
import { openSupportChatwoot } from '../chatwoot';
import {
  fetchFeedbackDetail,
  fetchFeedbackImageUrl,
  markFeedbackRead,
  type FeedbackDetail as FeedbackDetailPayload,
} from './api';
import { isFeedbackUnread } from './feedbackStatus';

/**
 * The detail screen for one feedback thread: the player's own submission on
 * top, the official reply (or an awaiting-reply placeholder) below, and a
 * "still need help" exit into Chatwoot — this is a single-round design, so
 * that link is the only way to keep the conversation going.
 *
 * Opening this screen is two requests, deliberately: fetchFeedbackDetail is
 * a read-only GET (the backend runs it in a read-only transaction and can't
 * write from inside it), so marking the thread read is a second call, fired
 * only when it is actually replied-and-unread, and failing silently — the
 * player already saw the reply, a bookkeeping error is not theirs to see.
 */
export function FeedbackDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const language = useI18nStore((state) => state.language);
  const t = useI18nStore((state) => state.t);
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['feedback-detail', token, id],
    queryFn: () => fetchFeedbackDetail(token || '', id),
    enabled: !!token,
  });

  // Guards a single mark-read attempt per mount (i.e. per "open"). A failed
  // attempt is retried the next time this thread is opened — a fresh mount
  // gives a fresh ref — never by looping inside this one.
  const markedRef = useRef<number | null>(null);
  useEffect(() => {
    const data = detailQuery.data;
    if (!token || !data) return;
    if (markedRef.current === id) return;
    if (!isFeedbackUnread(data)) return;
    markedRef.current = id;
    markFeedbackRead(token, id)
      .then(() => {
        // Both queries invalidated together so the row's dot and the tab's
        // badge clear in the same render — clearing only one would leave
        // them disagreeing on screen.
        queryClient.invalidateQueries({ queryKey: ['feedback'] });
        queryClient.invalidateQueries({ queryKey: ['feedback-summary'] });
      })
      .catch((error) => {
        // Silent by design: the reply is already on screen, so surfacing a
        // read-tracking failure would be noise the player didn't ask for.
        console.error('markFeedbackRead failed', error);
      });
  }, [detailQuery.data, id, token, queryClient]);

  // <img src> can't carry the Authorization header, so each image is fetched
  // as a blob and handed back as an object URL — which this effect must
  // revoke on cleanup, or every screenshot ever viewed this session leaks.
  const [imageUrls, setImageUrls] = useState<Record<number, string>>({});
  useEffect(() => {
    const images = detailQuery.data?.images ?? [];
    if (!token || images.length === 0) {
      setImageUrls({});
      return;
    }
    let cancelled = false;
    const created: Record<number, string> = {};

    images.forEach((image) => {
      fetchFeedbackImageUrl(token, image.id)
        .then((url) => {
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          created[image.id] = url;
          setImageUrls((previous) => ({ ...previous, [image.id]: url }));
        })
        .catch((error) => {
          console.error('fetchFeedbackImageUrl failed', error);
        });
    });

    return () => {
      cancelled = true;
      Object.values(created).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, detailQuery.data?.images]);

  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [chatOpening, setChatOpening] = useState(false);

  async function handleStillNeedHelp() {
    setChatOpening(true);
    try {
      await openSupportChatwoot({ user, language, route: 'avisos.feedback' });
    } catch {
      // Best effort — the Help tab's chat card is the primary retry surface
      // for a Chatwoot failure, this is a secondary entry point into it.
    } finally {
      setChatOpening(false);
    }
  }

  const data = detailQuery.data;

  return (
    <div className="feedback-detail">
      <header className="feedback-detail-header">
        <button type="button" className="icon-button" onClick={onBack} aria-label={t('back')}>
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <h2>{t('feedbackMine')}</h2>
      </header>

      {detailQuery.isLoading ? (
        <div className="feedback-state">
          <strong>{t('loading')}</strong>
        </div>
      ) : null}

      {detailQuery.isError ? (
        <div className="feedback-state">
          <AlertTriangle size={28} aria-hidden="true" />
          <strong>{t('avisosLoadError')}</strong>
          <p>{t('avisosLoadErrorBody')}</p>
          <button type="button" onClick={() => detailQuery.refetch()}>
            {t('retry')}
          </button>
        </div>
      ) : null}

      {data ? (
        <>
          <section className="feedback-detail-block">
            <p className="feedback-detail-content">{data.content}</p>
            {data.images.length > 0 ? (
              <div className="feedback-image-grid">
                {data.images.map((image, index) => (
                  <button
                    key={image.id}
                    type="button"
                    className="feedback-image-thumb"
                    onClick={() => setViewerIndex(index)}
                    disabled={!imageUrls[image.id]}
                  >
                    {imageUrls[image.id] ? <img src={imageUrls[image.id]} alt="" /> : null}
                  </button>
                ))}
              </div>
            ) : null}
            <em className="feedback-detail-time">{formatLocalTime(data.created_at)}</em>
          </section>

          <section className={`feedback-reply-block ${data.status === 'replied' ? 'is-replied' : 'is-pending'}`}>
            {data.status === 'replied' ? (
              <>
                <h3>
                  <Check size={16} aria-hidden="true" />
                  {t('feedbackOfficialReply')}
                </h3>
                <p>{data.reply_content}</p>
                {data.replied_at ? <em>{formatLocalTime(data.replied_at)}</em> : null}
              </>
            ) : (
              <p className="feedback-awaiting">
                <Clock size={16} aria-hidden="true" />
                {t('feedbackAwaitingReply')}
              </p>
            )}
          </section>

          <button
            type="button"
            className="feedback-still-need-help"
            disabled={chatOpening}
            onClick={handleStillNeedHelp}
          >
            {t('feedbackStillNeedHelp')}
          </button>
        </>
      ) : null}

      {viewerIndex != null && data ? (
        <FeedbackImageViewer
          images={data.images}
          urls={imageUrls}
          startIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * doc/SUPPORT_CENTER_PLAN.md §0.4: swipe only, deliberately no pinch-zoom —
 * these screenshots only need confirming, not inspecting pixel-by-pixel, and
 * the app's motion budget (discipline 5) is reserved for four whitelisted
 * celebratory moments this isn't one of. A horizontal scroll-snap track
 * gives swipe navigation for free without any gesture code.
 */
function FeedbackImageViewer({
  images,
  urls,
  startIndex,
  onClose,
}: {
  images: FeedbackDetailPayload['images'];
  urls: Record<number, string>;
  startIndex: number;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const [current, setCurrent] = useState(startIndex);
  const trackRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = trackRef.current;
    if (!node) return;
    node.scrollTo({ left: node.clientWidth * startIndex, behavior: 'auto' });
    // Runs once, on open, to jump to the tapped thumbnail — not on every
    // `startIndex` identity a parent re-render might produce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleScroll() {
    const node = trackRef.current;
    if (!node || node.clientWidth === 0) return;
    const index = Math.round(node.scrollLeft / node.clientWidth);
    setCurrent(Math.min(images.length - 1, Math.max(0, index)));
  }

  return (
    <div className="feedback-image-viewer" role="dialog" aria-modal="true">
      <div className="feedback-image-viewer-top">
        <span className="feedback-image-viewer-count">
          {current + 1}/{images.length}
        </span>
        <button type="button" className="feedback-image-viewer-close" onClick={onClose} aria-label={t('close')}>
          <X size={20} aria-hidden="true" />
        </button>
      </div>
      <div className="feedback-image-viewer-track" ref={trackRef} onScroll={handleScroll}>
        {images.map((image) => (
          <div className="feedback-image-viewer-slide" key={image.id}>
            {urls[image.id] ? <img src={urls[image.id]} alt="" /> : null}
          </div>
        ))}
      </div>
    </div>
  );
}
